/**
 * UserProtectedRoute - Guard pour les routes utilisateur (basé sur Redux)
 *
 * Ce composant protège les routes utilisateur et redirige vers /login
 * si l'utilisateur n'est pas authentifié.
 *
 * Il utilise directement le Redux store (authSlice) pour l'état d'authentification.
 * Il est complètement isolé de l'authentification admin.
 */

import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { checkAuthStatus, clearUserSession } from "@/store/slices/authSlice";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { canAccessDashboard } from "@/utils/subscription-status";

interface UserProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
}

// Variables globales pour éviter les vérifications et toasts multiples
let isVerifying = false;
let verificationPromise: Promise<boolean> | null = null;
let toastShown = false;

export const UserProtectedRoute: React.FC<UserProtectedRouteProps> = ({
  children,
  requireSubscription = false,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // État Redux pour l'authentification user
  const {
    user,
    isAuthenticated,
    isLoading: reduxLoading,
  } = useAppSelector((state) => state.auth);

  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const hasChecked = useRef(false);

  // Ne pas gérer les routes admin
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    // Ignorer les routes admin
    if (isAdminRoute) {
      setIsChecking(false);
      return;
    }

    // Si déjà vérifié avec succès et authentifié
    if (hasChecked.current && isAuthenticated && user) {
      setIsChecking(false);
      return;
    }

    const runVerification = async () => {
      // Si une vérification est déjà en cours, attendre son résultat
      if (isVerifying && verificationPromise) {
        const result = await verificationPromise;
        if (result) {
          hasChecked.current = true;
        } else {
          setShouldRedirect(true);
        }
        setIsChecking(false);
        return;
      }

      // Démarrer une nouvelle vérification
      isVerifying = true;
      verificationPromise = (async () => {
        try {
          await dispatch(checkAuthStatus()).unwrap();
          // Reset le flag de toast si la connexion réussit
          toastShown = false;
          return true;
        } catch {
          return false;
        }
      })();

      try {
        const isValid = await verificationPromise;

        if (isValid) {
          hasChecked.current = true;
        } else {
          // Session invalide - UN SEUL toast
          if (!toastShown) {
            toastShown = true;
            toast.error(t("notifications.sessionExpired"), {
              description: t("notifications.sessionExpiredDescription"),
              duration: 3000,
            });
          }
          dispatch(clearUserSession());
          setShouldRedirect(true);
        }
      } finally {
        isVerifying = false;
        verificationPromise = null;
        setIsChecking(false);
      }
    };

    runVerification();
  }, [dispatch, isAuthenticated, user, isAdminRoute, t]);

  // Ne pas interférer avec les routes admin
  if (isAdminRoute) {
    return <>{children}</>;
  }

  // Afficher un loader pendant la vérification
  if (isChecking || reduxLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Rediriger vers login si non authentifié
  if (shouldRedirect || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Vérifier l'abonnement si requis
  if (requireSubscription && !canAccessDashboard(user)) {
    return <Navigate to="/plans" replace />;
  }

  return <>{children}</>;
};

// Fonction pour reset le flag de toast (utile après une connexion réussie)
export const resetUserSessionToast = () => {
  toastShown = false;
};

export default UserProtectedRoute;
